import React, { useState } from 'react';
import api from '../services/api';

/**
 * Report a problem, from wherever the person already is.
 *
 * The mailto this replaces collected nothing in months. It asked someone on a
 * phone, mid-frustration, to leave the app and compose an email describing a
 * bug from memory. This asks for one sentence and captures the rest.
 *
 * Two deliberate choices:
 *
 *   No file upload, and an explicit line asking for no results. A free text box
 *   in a health app attracts pasted lab values, and the honest mitigation is to
 *   not invite them.
 *
 *   It never blocks on being signed in. The report we most need is from someone
 *   who could not get in, and that is exactly the person a login wall rejects.
 */
const CATEGORIES = [
  { id: 'bug', label: 'Something is broken' },
  { id: 'confusing', label: 'Something is confusing' },
  { id: 'suggestion', label: 'I have a suggestion' },
  { id: 'other', label: 'Something else' },
];

const MAX = 4000;

export default function ReportProblem() {
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState('bug');
  const [message, setMessage] = useState('');
  const [state, setState] = useState({ status: 'idle' });

  const submit = async (e) => {
    e.preventDefault();
    if (message.trim().length < 10 || state.status === 'sending') return;
    setState({ status: 'sending' });
    try {
      await api.post('/problem-report', {
        category,
        message: message.trim(),
        page: window.location.pathname,
      });
      setState({ status: 'sent' });
      setMessage('');
    } catch (err) {
      setState({
        status: 'error',
        // The server's own wording when it has one — it is more specific than
        // anything generic we could substitute.
        message: err?.response?.data?.error
          || 'We could not send that just now. Please try again in a moment.',
      });
    }
  };

  if (state.status === 'sent') {
    return (
      <div className="mt-8 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-xl p-5">
        <p className="font-semibold text-emerald-800 dark:text-emerald-300 text-sm">Thank you — we have it.</p>
        <p className="text-sm text-slate-600 dark:text-slate-300 mt-1">
          A person reads every one of these. If we need more detail we will email you.
        </p>
        <button
          onClick={() => { setState({ status: 'idle' }); setOpen(true); }}
          className="text-sm text-emerald-700 dark:text-emerald-400 font-medium hover:underline mt-3"
        >
          Report something else
        </button>
      </div>
    );
  }

  if (!open) {
    return (
      <div className="mt-8 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-5">
        <p className="font-semibold text-slate-800 dark:text-slate-100 text-sm">Something not working?</p>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
          Tell us what happened and we will look into it.
        </p>
        <button
          onClick={() => setOpen(true)}
          className="mt-3 bg-sky-600 hover:bg-sky-700 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors"
        >
          Report a problem
        </button>
      </div>
    );
  }

  const tooShort = message.trim().length > 0 && message.trim().length < 10;

  return (
    <form onSubmit={submit} className="mt-8 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-5">
      <p className="font-semibold text-slate-800 dark:text-slate-100 text-sm">Report a problem</p>

      <fieldset className="mt-4">
        <legend className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-2">What kind of problem?</legend>
        <div className="flex flex-wrap gap-2">
          {CATEGORIES.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => setCategory(c.id)}
              aria-pressed={category === c.id}
              className={`text-sm px-3 py-1.5 rounded-lg border transition-colors ${
                category === c.id
                  ? 'bg-sky-50 border-sky-400 text-sky-800 dark:bg-sky-900/40 dark:border-sky-600 dark:text-sky-200'
                  : 'border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:border-slate-400'
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>
      </fieldset>

      <label htmlFor="problem-message" className="block text-xs font-medium text-slate-500 dark:text-slate-400 mt-4 mb-1.5">
        What happened?
      </label>
      <textarea
        id="problem-message"
        value={message}
        onChange={(e) => setMessage(e.target.value.slice(0, MAX))}
        rows={5}
        placeholder="I tried to upload my results and the page stayed on the spinner."
        className="w-full rounded-lg border border-slate-300 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
      />

      <div className="flex items-start justify-between gap-4 mt-1.5">
        <p className="text-xs text-slate-400 dark:text-slate-500">
          Please don’t include your results or any health details — we only need to know what went wrong.
        </p>
        <span className="text-xs text-slate-400 tabular-nums shrink-0">{message.length}/{MAX}</span>
      </div>

      {tooShort && (
        <p className="text-xs text-amber-600 mt-2">A sentence or two helps us find it.</p>
      )}
      {state.status === 'error' && (
        <p className="text-sm text-rose-600 mt-2">{state.message}</p>
      )}

      <p className="text-xs text-slate-400 dark:text-slate-500 mt-3">
        We’ll automatically include which page you were on and what browser you’re using.
      </p>

      <div className="flex items-center gap-3 mt-4">
        <button
          type="submit"
          disabled={message.trim().length < 10 || state.status === 'sending'}
          className="bg-sky-600 hover:bg-sky-700 disabled:opacity-40 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors"
        >
          {state.status === 'sending' ? 'Sending…' : 'Send report'}
        </button>
        <button
          type="button"
          onClick={() => { setOpen(false); setState({ status: 'idle' }); }}
          className="text-sm text-slate-500 dark:text-slate-400 hover:underline"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
